/**
 * cart-fly.js — Pixxiesx
 *
 * Fly-to-cart + badge spring-pop.
 *
 * On every add-to-cart click (outside the cart drawer):
 *  1. A colored dot flies immediately from the button to the cart icon.
 *  2. The cart badge springs once the cart update succeeds.
 *  3. The cart drawer opens if it is not already open.
 *
 * Theme customization:
 *   :root { --cart-fly-color: #FFD800; }   ← change the dot color
 *
 * Respects prefers-reduced-motion (animations skipped, count still updates).
 * Falls back to Web Animations API when Motion is not yet loaded.
 */
;(function () {
  'use strict';

  var FLY_COLOR_VAR = '--cart-fly-color';
  var DEFAULT_COLOR = '#0A295D';   // Pixxiesx navy
  var FLY_MS        = 520;         // total flight duration in ms
  var SOURCE_TTL    = 5000;        // ms — discard a stale button position
  var flySource = null;           // { x, y } center of the last clicked ATC button
  var flyTs     = 0;              // timestamp of that click
  var instantFlightPending = false;
  var DOT_SELECTOR = '[data-cart-fly-dot]';

  // ─── 1. Capture the add-to-cart button position (capture phase) ───────────
  //   Runs before the form's own handler so the position is available even if
  //   the handler swaps DOM immediately. Ignores buttons inside the cart
  //   drawer (upsell / gift-bag adds don't need a fly animation).
  document.addEventListener('click', function (e) {
    var btn = e.target.closest(
	      'product-form [type="submit"], ' +
	      '[add-to-cart-button], '         +
	      '[toggle-element="cart-drawer"], ' +
	      '[data-fly-source]'
	    );
    if (!btn) return;
    if (btn.closest('cart-drawer-component')) return;

    var r     = btn.getBoundingClientRect();
    flySource = { x: r.left + r.width  / 2,
                  y: r.top  + r.height / 2 };
    flyTs     = Date.now();

    // Open as early as possible, independent from the specific ProductForm /
    // ProductCard handler. Those handlers still own success/error reconciliation.
    openDrawerIfClosed();

    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      var tgt = getCartTarget();
      if (tgt) {
        removeStaleDots();
        launchDot(flySource, tgt, { popBadge: false, openDrawer: false });
        instantFlightPending = true;
      }
    }
  }, true);

  // ─── 2. Launch fly on successful cart update ──────────────────────────────
  //   _EVENT_HELPER.triggerCartChange fires after every successful cart
  //   mutation (add / remove / qty change). We only fly when a button was
  //   clicked recently (within SOURCE_TTL), guaranteeing no stray animations
  //   from programmatic cart updates.
  document.addEventListener(_EVENT_HELPER.triggerCartChange, function () {
    if (!flySource || Date.now() - flyTs > SOURCE_TTL) return;

    if (instantFlightPending) {
      instantFlightPending = false;
      flySource = null;
      popBadge();
      openDrawerIfClosed();
      return;
    }

    var src   = flySource;
    flySource = null;   // consume — one fly per add

    // Honor reduced-motion preference (count badge still updates via Section
    // Rendering — we only skip the decorative animation here).
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      popBadge();
      openDrawerIfClosed();
      return;
    }

    var tgt = getCartTarget();
    if (!tgt) return;

    launchDot(src, tgt);
  });

  // ─── 3. Helpers ───────────────────────────────────────────────────────────
  function getFlyColor() {
    var v = getComputedStyle(document.documentElement)
              .getPropertyValue(FLY_COLOR_VAR).trim();
    return v || DEFAULT_COLOR;
  }

  function getCartTarget() {
    var el = getFirstVisibleElement([
      '[header-cart-bubble]',
      '.header__icon[drawer-to-open="cart-drawer"]',
      '.header__icon[href$="/cart"]'
    ]);
    if (!el) return null;
    var r  = el.getBoundingClientRect();
    return { x: r.left + r.width  / 2,
             y: r.top  + r.height / 2 };
  }

  function getFirstVisibleElement(selectors) {
    for (var i = 0; i < selectors.length; i++) {
      var elements = document.querySelectorAll(selectors[i]);
      for (var j = 0; j < elements.length; j++) {
        var el = elements[j];
        var r = el.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) return el;
      }
    }
    return null;
  }

  // ─── 4. Fly dot ───────────────────────────────────────────────────────────
  function launchDot(src, tgt, options) {
    options = options || {};
    var shouldPopBadge = options.popBadge !== false;
    var shouldOpenDrawer = options.openDrawer !== false;
    var dx   = tgt.x - src.x;
    var dy   = tgt.y - src.y;
    // Arc peak: travel upward past the target before curving in.
    // When the target is already above the source (dy < 0) we overshoot
    // further; when it is below we still arc upward first.
    var arcY = Math.min(dy - 80, -60);

    var dot = document.createElement('div');
    dot.setAttribute('aria-hidden', 'true');
    dot.setAttribute('data-cart-fly-dot', '');
    // Position the dot so its CENTER sits on the button center (offset by r/2).
    dot.style.cssText =
      'position:fixed;z-index:99999;' +
      'width:12px;height:12px;border-radius:50%;' +
      'pointer-events:none;will-change:transform,opacity;' +
      'background:' + getFlyColor() + ';' +
      'left:' + (src.x - 6) + 'px;' +
      'top:'  + (src.y - 6) + 'px;';

    document.body.appendChild(dot);

    var done = false;
    var onArrive = function () {
      if (done) return;
      done = true;
      dot.remove();
      if (shouldPopBadge) popBadge();
      if (shouldOpenDrawer) openDrawerIfClosed();
    };

    // Use WAAPI for the flight itself. It is native, survives theme Motion
    // sequence edge-cases, and gives us a deterministic finish/cancel cleanup.
    var a = dot.animate(
      [
        { transform: 'translate3d(0,0,0) scale(1)',
          opacity: 1 },
        { transform: 'translate3d(' + (dx * 0.5) + 'px,' + arcY + 'px,0) scale(0.85)',
          opacity: 1, offset: 0.45 },
        { transform: 'translate3d(' + dx + 'px,' + dy + 'px,0) scale(0.3)',
          opacity: 0.7 }
      ],
      { duration: FLY_MS, easing: 'cubic-bezier(0.4,0,0.2,1)', fill: 'forwards' }
    );
    a.addEventListener('finish', onArrive, { once: true });
    a.addEventListener('cancel', onArrive, { once: true });
    setTimeout(onArrive, FLY_MS + 180);
  }

  function removeStaleDots() {
    document.querySelectorAll(DOT_SELECTOR).forEach(function (dot) {
      dot.getAnimations().forEach(function (animation) {
        try { animation.cancel(); } catch (e) {}
      });
      dot.remove();
    });
  }

  // ─── 5. Badge spring pop ──────────────────────────────────────────────────
  //   Targets the [header-cart-bubble] container (stable across Section
  //   Rendering swaps — the inner span is replaced but the wrapper div stays).
  function popBadge() {
    var targets = [].slice.call(
      document.querySelectorAll('[header-cart-bubble], [cart-item-count]')
    );
    if (!targets.length) return;

    if (typeof Motion !== 'undefined') {
      Motion.animate(
        targets,
        { scale: [1, 1.55, 0.85, 1.08, 1] },
        { duration: 0.42, easing: 'cubic-bezier(.36,.07,.19,.97)' }
      );
    } else {
      var kf  = [
        { transform: 'scale(1)'    },
        { transform: 'scale(1.55)' },
        { transform: 'scale(0.85)' },
        { transform: 'scale(1.08)' },
        { transform: 'scale(1)'    }
      ];
      var opt = { duration: 420, easing: 'cubic-bezier(.36,.07,.19,.97)' };
      targets.forEach(function (el) { el.animate(kf, opt); });
    }
  }

  // ─── 6. Drawer open (safety net) ─────────────────────────────────────────
  //   ProductForm/ProductCard already open the drawer instantly when the request
  //   starts. This is a fallback for any ATC path that does not go through that
  //   standard optimisticCartOpen flow.
  //   Guard: if the drawer opened already (isDrawerOpen = true) this is a
  //   no-op, so there is no double-open risk.
  function openDrawerIfClosed() {
    var drawer = document.querySelector('cart-drawer-component');
    if (!drawer) return;
    if (drawer.isDrawerOpen || drawer.classList.contains('drawer-active') || drawer.__cartFlyOpening) return;
    drawer.__cartFlyOpening = true;
    HELPER_UTIL.dispatchCustomEvent(_EVENT_HELPER.showDrawer, document, {
      drawerType: 'cart-drawer',
    });
    setTimeout(function () {
      var freshDrawer = document.querySelector('cart-drawer-component');
      if (freshDrawer) freshDrawer.__cartFlyOpening = false;
    }, 450);
  }

})();
